import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, lifecycle, onlyUpdateForKeys } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { modal } from 'ui/shared/modal';
import { DeviceIndex, StreamId, DeviceMap, DeviceId } from 'types';
import { State } from 'reducers';
import { getDevices, isLoadingDevices, isCamera } from 'selectors/deviceSelectors';
import { deviceIdsFromStreamIds, defaultStreamIdsFromDeviceIds } from 'util/streamId';
import { ReportDevice } from 'logic/reports/ReportDevice';
import { CameraTree } from 'ui/shared/CameraTree';
import { CameraFilter } from 'ui/shared/CameraFilter';
import { Button } from 'ui/shared/Button';
import { unique } from 'util/unique';

import './AddCamerasDialog.css';

export type OwnProps = {
    visible: boolean;
    selected: ReadonlyArray<DeviceId>;
    hiddenIds?: ReadonlyArray<DeviceId>;
    multiselect?: boolean;
    onAccept: (selectedCameras: ReadonlyArray<DeviceId>) => void;
    onCancel: () => void;
};

export const RawAddCamerasDialog = ({
    multiselect = false,
    selectedStreamIds,
    filterText,
    handleChangeFilter,
    filteredStreamIds,
    handleSelectionChanged,
    handleAccept,
    onCancel,
    handleAdd,
    handleRemove,
    handleSelectAll,
    handleSelectNone,
    styles,
    t,
    ...props
}: OwnProps & RecomposeProps & OemProps & TranslateProps) =>
    <Fragment>
        <h2>
            { t('SERVER_CONFIG_BTN_ADD_CAMERA_plural') }
        </h2>
        <CameraFilter
            id="add-cameras-filter"
            filterText={ filterText }
            onChange={ handleChangeFilter }
        />
        <CameraTree
            id="add-cameras-tree"
            streamIds={ filteredStreamIds }
            checkedStreamIds={ selectedStreamIds }
            selectedStreamId={ null }
            select={ multiselect ? 'checkbox' : 'radio' }
            showStreams={ false }
            onCheckChanged={ handleSelectionChanged }
        />
        { multiselect &&
            <div className="add-cameras-left-buttons">
                <Button
                    id="add-cameras-select-all-button"
                    type="button"
                    onClick={ handleSelectAll }
                    textKey="6.1237.3347"
                />
                <Button
                    id="add-cameras-select-none-button"
                    type="button"
                    onClick={ handleSelectNone }
                    textKey="WC_BUTTON_SELECT_NONE"
                />
            </div>
        }
        <div className="add-cameras-right-buttons">
            <Button
                id="add-cameras-ok-button"
                type="button"
                onClick={ handleAccept }
                textKey="1"
            />
            <Button
                id="add-cameras-cancel-button"
                type="button"
                onClick={ onCancel }
                textKey="290"
            />
        </div>
    </Fragment>
;

type StateProps = {
    devices: DeviceMap | null;
    loading: boolean;
};
type RecomposeProps = {
    selectedStreamIds: ReadonlyArray<DeviceId>;
    filterText: string;
    filteredStreamIds: ReadonlyArray<StreamId>;
    handleChangeFilter: (filterText: string) => void;
    handleAdd: (device: ReportDevice) => void;
    handleRemove: (deviceIndex: DeviceIndex) => void;
    handleAccept: () => void;
    handleSelectAll: () => void;
    handleSelectNone: () => void;
    handleSelectionChanged: (checked: ReadonlyArray<StreamId>) => void;
};
type _RecomposeProps = RecomposeProps & {
    _setFilteredStreamIds: (streamIds: ReadonlyArray<StreamId>) => void;
};

const mapStateToProps = (state: State) => ({
    devices: getDevices(state),
    loading: isLoadingDevices(state),
});

export function filterVisibleCameras(devices: DeviceMap | null, hiddenIds: ReadonlyArray<DeviceId>, filterText: string): ReadonlyArray<StreamId> {
    let deviceIds = Object.keys(devices || {}).filter(id =>
        isCamera(devices![id]) &&
        hiddenIds.indexOf(id) < 0
    );

    if (filterText) {
        // Create a regex from the filter text... escape special characters
        const re = new RegExp(filterText.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
        deviceIds = deviceIds.filter(id =>
            re.test(devices![id].name)
        );
    }

    return defaultStreamIdsFromDeviceIds(deviceIds);
}

export const AddCamerasDialog = compose<OwnProps & RecomposeProps, OwnProps>(
    setDisplayName('AddCamerasDialog'),
    skin,
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withState('selectedStreamIds', 'handleSelectionChanged', []),
    withState('filterText', 'handleChangeFilter', ''),
    withState('filteredStreamIds', '_setFilteredStreamIds', []),
    withHandlers<OwnProps & RecomposeProps, Partial<RecomposeProps>>({
        handleAccept: ({ onAccept, selectedStreamIds }) => () => {
            const deviceIds = deviceIdsFromStreamIds(selectedStreamIds);
            onAccept(deviceIds);
        },
        handleSelectAll: ({ filteredStreamIds, selectedStreamIds, handleSelectionChanged }) => () => {
            // Add the filtered stream IDs to the set of selected stream IDs
            const newArray = unique([
                ...selectedStreamIds,
                ...filteredStreamIds,
            ]);
            handleSelectionChanged(newArray);
        },
        handleSelectNone: ({ filteredStreamIds, selectedStreamIds, handleSelectionChanged }) => () => {
            // Remove the filtered stream IDs from the set of selected stream IDs
            const toRemove = filteredStreamIds.reduce((o, id) => { o[id] = true; return o; }, {});
            const newArray = selectedStreamIds.filter(id => !toRemove[id]);
            handleSelectionChanged(newArray);
        },
    }),
    lifecycle<OwnProps & StateProps & _RecomposeProps, {}>({
        componentDidMount() {
            // Set the initial filtered list of cameras
            const { devices, hiddenIds = [], filterText, _setFilteredStreamIds } = this.props;
            const streamIds = filterVisibleCameras(devices, hiddenIds, filterText);
            _setFilteredStreamIds(streamIds);
        },
        componentDidUpdate(oldProps: OwnProps & StateProps & _RecomposeProps) {
            // Update the list of visible cameras when either the devices or the filter text changes
            if (this.props.devices !== oldProps.devices ||
                this.props.hiddenIds !== oldProps.hiddenIds ||
                this.props.filterText !== oldProps.filterText
            ) {
                const { devices, hiddenIds = [], filterText, _setFilteredStreamIds } = this.props;
                const streamIds = filterVisibleCameras(devices, hiddenIds, filterText);
                _setFilteredStreamIds(streamIds);
            }

            // Reset the filter text when the dialog is shown
            // (don't reset it when hiding because we don't want it to change while the dialog is fading out)
            if (this.props.visible && !oldProps.visible) {
                const { selected, handleChangeFilter, handleSelectionChanged } = this.props;
                const streamIds = defaultStreamIdsFromDeviceIds(selected);
                handleChangeFilter('');
                handleSelectionChanged(streamIds);
            }
        },
    }),
    onlyUpdateForKeys([ 'visible', 'devices', 'filterText', 'filteredStreamIds', 'selectedStreamIds' ]),
    modal('add-cameras-dialog'),
)(RawAddCamerasDialog);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/cameras/AddCamerasDialog.tsx